import React, { useState, useEffect } from "react";
import { Heart, Star, Rainbow, Gift } from "lucide-react";
import { Link } from "react-router-dom";
import { StoreInfo, Category } from "../types";
import { getEffectiveFeatureFlags } from "../utils/featureFlags";
import { dataService } from "../services/dataService";
import PlayfulLoader from "./PlayfulLoader";

interface FooterProps {
  storeInfo: StoreInfo;
}

export const Footer: React.FC<FooterProps> = ({ storeInfo }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  // Get feature flags
  const featureFlags = getEffectiveFeatureFlags();

  // Fetch categories from API
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const response = await dataService.getCategories();
        if (response.success && response.data) {
          // Filter out categories with no products and limit to top 5
          const categoriesWithProducts = response.data
            .filter((cat) => (cat.productCount || 0) > 0)
            .slice(0, 5);
          setCategories(categoriesWithProducts);
        }
      } catch (error) {
        console.error("Error loading categories for footer:", error);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  const quickLinks = [
    { label: "Home", href: "/" },
    { label: "Categories", href: "/categories" },
    { label: "Products", href: "/products" },
    ...(featureFlags.showOffers
      ? [{ label: "Special Offers", href: "/offers" }]
      : []),
    ...(featureFlags.showVideos ? [{ label: "Videos", href: "/videos" }] : []),
    { label: "Contact", href: "/contact" },
  ];

  return (
    <footer className="bg-gradient-to-r from-purple-900 to-pink-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                🧸
              </div>
              <span className="ml-2 text-xl font-bold">{storeInfo.name}</span>
            </div>

            <p className="text-purple-200 mb-4 leading-relaxed">
              Bringing joy and imagination to children worldwide through amazing
              toys and unforgettable experiences.
            </p>

            <div className="flex items-center text-purple-200">
              <Heart className="h-4 w-4 mr-2 text-pink-400" />
              <span className="text-sm">Made with love for kids</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-purple-200 hover:text-white transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Categories</h3>
            {isLoadingCategories ? (
              <div className="py-4">
                <PlayfulLoader size="sm" variant="bounce" />
              </div>
            ) : categories.length > 0 ? (
            <ul className="space-y-2">
              {categories.map((category) => (
                  <li key={category.id}>
                    <Link
                      to={`/category/${category.id}`}
                      className="text-purple-200 hover:text-white transition-colors duration-200"
                    >
                      {category.name}
                    </Link>
                </li>
              ))}
            </ul>
            ) : (
              <p className="text-purple-200 text-sm">No categories available</p>
            )}
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-purple-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            {/* Copyright */}
            <div className="mb-4 md:mb-0">
              <p className="text-purple-200 text-sm">
                © {new Date().getFullYear()} {storeInfo.name}. All rights
                reserved.
              </p>
            </div>

            {/* Decorative Elements */}
            <div className="flex items-center space-x-2 text-purple-200">
              <Star className="h-4 w-4" />
              <Rainbow className="h-4 w-4" />
              <Gift className="h-4 w-4" />
              <span className="text-sm ml-2">
                Spreading joy one toy at a time!
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
